import * as THREE from 'three';
import { FACTION_COLOR, SHRINE_POWER_MAX, type Element, type Faction } from '../core/constants.ts';
import type { SimEvent, SimState } from '../core/sim.ts';
import type { Vec3 } from '../core/math.ts';
import { ELEMENT_COLOR } from './palette.ts';
import { SpellVfx } from './vfx.ts';
import { MageAnimator } from './mageAnimator.ts';
import { Nameplates } from './nameplates.ts';
import { DamageNumbers } from './damageNumbers.ts';
import { lodFor, setShadowCasting } from './mageLod.ts';

/**
 * Every role whose model is warmed during loading.
 *
 * Exported so a test can check it against the manifest. A role with a file
 * behind it and no entry here compiles its shader the first time it is walked
 * past, which is one microstutter per prop per session and reads as the loading
 * screen not working — the fault this list exists to prevent, and one that
 * returns quietly the moment somebody adds a model without adding it here.
 *
 * Mages are absent on purpose: they are warmed as whole actors elsewhere,
 * skeleton and all, which a bare `create` would not cover.
 */
export const WARMED_ROLES: readonly AssetRole[] = [
  'level.crate', 'level.bookshelf', 'level.pillar',
  'prop.torch', 'prop.shrine', 'prop.pool',
  // The cathedral's furniture. A model uploads to the card on its first *draw*,
  // so a bench nobody has looked at yet is a hitch waiting for whoever turns a
  // corner — which is the whole reason this list exists.
  'prop.bench', 'prop.cauldron', 'prop.workbench', 'prop.crateMetal',
  'prop.crateFarm', 'prop.arch', 'prop.cage', 'prop.roofSupport', 'prop.chain',
  'projectile.mace', 'projectile.blade', 'projectile.shard',
  'projectile.stone', 'projectile.mass', 'projectile.barb', 'projectile.orb',
];

/**
 * How solid a ghost looks.
 *
 * Raised from a third. A ghost has to read as dead at a glance and *also* be
 * findable — a Cleric's whole job at that moment is walking to one, and at a
 * third of an opacity across a lit nave they were indistinguishable from the
 * living at range and nearly invisible against a bright wall. Being pale, cold
 * and floating already says dead; the transparency only needs to help.
 */
const GHOST_OPACITY = 0.38;

/**
 * What a ghost is tinted.
 *
 * Not black — a dark silhouette in a dark cathedral is invisible rather than
 * ghostly. A pale, slightly cool grey reads as *drained* against the warm stone
 * and the torchlight, which is the thing being said.
 */
const GHOST_GREY = 0xb8bcc4;

/**
 * One discharge on screen: an arc between two mages, or a bolt down a lane.
 *
 * Held in a pool and handed out, never rebuilt. `axis` is what tells the two
 * apart — a bolt keeps pointing where it was fired and is spun to face the eye,
 * where an arc is a line between two points and does not care.
 */
interface Flash {
  line: THREE.Object3D;
  head: THREE.Mesh;
  life: number;
  axis?: THREE.Vector3;
  /** Set for bolts: the pooled light thrown on whatever it struck. */
  lamp?: THREE.PointLight;
}

/**
 * The cards a beam is built from, widest and faintest first.
 *
 * Three, at different thicknesses and drifting at different rates. The eye reads
 * the disagreement between them as depth and movement; one card alone is a decal
 * of lightning rather than lightning. `scroll` is turns of the strip per second,
 * and the middle one runs backwards so the layers never sync up.
 */
const BOLT_LAYERS = [
  { width: 2.1, opacity: 0.35, scroll: 0.9 },
  { width: 1.15, opacity: 0.6, scroll: -1.6 },
  { width: 0.55, opacity: 0.95, scroll: 2.4 },
];

/**
 * How many bolt impacts may be lit at once.
 *
 * Built with the scene and never added to, like every other light in the game —
 * the count is part of a material's program signature, so one created on a cast
 * recompiles the arena. Six is more simultaneous bolts than a fight produces;
 * a seventh takes the oldest lamp, which nobody sees.
 */
const BOLT_LIGHT_BUDGET = 6;
/** How brightly an impact burns, and how far it carries. */
const BOLT_LIGHT = 26;
const BOLT_LIGHT_RANGE = 9;

/** How many of each kind may be alight before the oldest is taken. */
const FLASH_BUDGET = 24;

/** Simultaneous storms that get a light of their own. */
const STORM_LIGHT_BUDGET = 4;
/** How brightly one burns. */
const STORM_LIGHT = 14;

/** How high a ghost rides above the floor. */
const GHOST_FLOAT = 0.55;
import { SPELLS_BY_ID } from '../core/spells.ts';
import { isEmbodied, type PlayerState } from '../core/player.ts';
import { AssetRegistry, eachMaterial } from './assets/registry.ts';
import { mageRole, type AssetRole } from './assets/roles.ts';


const NEUTRAL = 0x6a6a6a;
/** How long an arc discharge stays on screen. */
const ARC_SECONDS = 0.25;

/**
 * How far a bolt wanders off the straight line between its ends, per element.
 *
 * The whole personality of the spell, in one number each. Lightning is all
 * elbows; a Void bolt barely wavers, because void is a tear in the air rather
 * than a discharge through it; stone hardly bends at all, since the fiction is a
 * thrown thing travelling too fast to see.
 */
/**
 * How much of a joint's displacement is carried over to the next one.
 *
 * The difference between a bolt and a saw blade. At zero every joint is
 * independent and the path alternates; near one it barely moves at all. Two
 * thirds is a lively wander that still arrives where it was aimed.
 */
/**
 * Which strip each beam is painted with.
 *
 * Keyed by element, with an override by spell id — the two Mentalist secondaries
 * are both `light` and are meant to read differently, so Lightning Bolt gets the
 * heavier, more forked strip that suits a spell you charge for a third of a
 * second and fire down a long lane.
 */
export const BOLT_ART: Record<string, string> = {
  fire: 'fire',
  earth: 'earth',
  physical: 'earth',
  light: 'light',
  cold: 'light',
  void: 'void',
  mana: 'mana',
  lightning_bolt: 'light_long',
};

/** How wide the card is, in metres. Heavier spells get a fatter beam. */
const BOLT_WIDTH: Partial<Record<Element, number>> = {
  light: 0.85,
  fire: 1.0,
  cold: 0.8,
  void: 1.1,
  mana: 0.9,
  earth: 0.7,
  physical: 0.7,
};



/** Every visual that has to track simulation state each frame. */
export class Actors {
  private readonly pools = new Map<string, {
    disc: THREE.Mesh; ring: THREE.Mesh; light: THREE.PointLight;
    /** Last sweep drawn, so an unchanged ring is not rebuilt. */
    sweep: number;
  }>();
  private readonly shrines = new Map<string, { crystal: THREE.Object3D; light: THREE.PointLight }>();
  private readonly storms = new Map<number, THREE.Group>();
  /**
   * Lights for storms, built once and never added to.
   *
   * Four is more simultaneous Firestorms than a match produces; a fifth simply
   * goes unlit, which is invisible next to four already burning.
   */
  private readonly stormLights: THREE.PointLight[] = [];
  /**
   * Lights for bolt impacts, built with the scene and never added to.
   *
   * The beam itself lights nothing — it is additive geometry, not a source — so
   * a bolt striking a wall in a dark nave used to leave the wall exactly as dark
   * as it was.
   */
  private readonly boltLights: THREE.PointLight[] = [];
  private nextBoltLight = 0;
  private readonly barriers = new Map<number, THREE.Mesh>();
  /** One original per kind of wall, cloned per wall. See `barrierMaterial`. */
  private readonly barrierKinds = new Map<string, THREE.MeshStandardMaterial>();
  private readonly signs = new Map<number, THREE.Group>();
  /** One per mage with a model behind it. Capsules have nothing to animate. */
  private readonly animators = new Map<string, MageAnimator>();
  /** Last shadow band each mage was in, so the tree is only walked on a change. */
  private readonly shadowState = new Map<string, boolean>();
  private readonly ghostState = new Map<string, boolean>();
  /** What each of a mage's materials was coloured before it died. */
  private readonly livingTint = new Map<string, Map<string, number>>();

  /** A stable per-mage phase, so a row of ghosts does not bob in unison. */
  private bob(id: string): number {
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) % 997;
    return (hash / 997) * Math.PI * 2;
  }
  private frame = 0;
  /** One of every effect the game can draw, kept alive to hold its program. */
  private readonly warm: THREE.Object3D[] = [];
  private readonly nameplates: Nameplates;
  private readonly numbers: DamageNumbers;
  private readonly vfx: SpellVfx;
  /** Per-mage flourishes that are not the mage: a wind-up aura, and flames. */
  private readonly auras = new Map<string, THREE.Mesh>();
  private readonly flames = new Map<string, THREE.Group>();
  /** Transient crackle for arc spells, which have no projectile to follow. */
  private readonly arcs: Flash[] = [];
  private readonly bolts = new Map<string, THREE.Texture>();
  private readonly bodies = new Map<string, THREE.Object3D>();
  /** The class and faction each mage's model was dressed for. */
  private readonly outfits = new Map<string, string>();

  private readonly scene: THREE.Scene;
  private readonly assets: AssetRegistry;
  private readonly camera?: THREE.Camera;

  constructor(
    scene: THREE.Scene,
    state: SimState,
    assets: AssetRegistry = AssetRegistry.grayBox(),
    camera?: THREE.Camera,
  ) {
    this.scene = scene;
    this.assets = assets;
    this.camera = camera;
    this.vfx = new SpellVfx(scene, assets);
    for (let i = 0; i < STORM_LIGHT_BUDGET; i++) {
      const light = new THREE.PointLight(0xffffff, 0, 12, 2);
      scene.add(light);
      this.stormLights.push(light);
    }
    for (let i = 0; i < BOLT_LIGHT_BUDGET; i++) {
      const light = new THREE.PointLight(0xffffff, 0, BOLT_LIGHT_RANGE, 2);
      scene.add(light);
      this.boltLights.push(light);
    }
    this.nameplates = new Nameplates(scene);
    this.numbers = new DamageNumbers(scene);
    for (const pool of state.arena.pools) {
      const disc = new THREE.Mesh(
        new THREE.CircleGeometry(pool.innerRadius, 32),
        new THREE.MeshStandardMaterial({
          color: NEUTRAL, emissive: NEUTRAL, emissiveIntensity: 0.5,
          transparent: true, opacity: 0.55, roughness: 0.3,
        }),
      );
      disc.rotation.x = -Math.PI / 2;
      disc.position.set(pool.pos.x, pool.pos.y + 0.06, pool.pos.z);
      scene.add(disc);

      const ring = new THREE.Mesh(
        new THREE.RingGeometry(pool.innerRadius + 0.15, pool.innerRadius + 0.5, 48, 1, 0, 0),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, side: THREE.DoubleSide }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(pool.pos.x, pool.pos.y + 0.08, pool.pos.z);
      scene.add(ring);

      const light = new THREE.PointLight(NEUTRAL, 2.5, 18, 2);
      light.position.set(pool.pos.x, pool.pos.y + 1.2, pool.pos.z);
      scene.add(light);

      this.pools.set(pool.id, { disc, ring, light, sweep: -1 });
    }

    for (const shrine of state.arena.shrines) {
      const colour = FACTION_COLOR[shrine.faction];
      const crystal = this.assets.create({ role: 'prop.shrine', color: colour });
      crystal.position.set(shrine.pos.x, shrine.pos.y + 2.4, shrine.pos.z);
      scene.add(crystal);

      const light = new THREE.PointLight(colour, 14, 40, 2);
      light.position.copy(crystal.position);
      scene.add(light);

      this.shrines.set(shrine.id, { crystal, light });
    }
  }

  /**
   * Draws one arc discharge, from an `arc` event. Jolts and Shock never miss and
   * never travel, so without this they are damage arriving from nowhere.
   */
  /**
   * A bolt: one stretched card, drawn from the eye to wherever it stopped.
   *
   * A hitscan leaves the renderer nothing to follow, so the beam *is* the spell —
   * it exists for a quarter of a second and that is the whole visual. Which
   * means it has to carry in one frame all the character a projectile would have
   * shown over its flight, and a polyline cannot: a line has no thickness, no
   * texture and no forking, so every bolt in the game looked like the same wire
   * in six colours.
   *
   * A painted strip stretched along the path carries all three at once, and puts
   * bolts in the same idiom as the darts — a drawing on a card, additive, with
   * the artist deciding what it looks like rather than a jitter constant.
   *
   * The card is billboarded about the beam's own axis rather than at the camera
   * outright: it has to keep pointing where it was fired, and only spin on that
   * axis to keep its face toward the eye. Turned fully to face the camera it
   * would stop being a beam and become a decal.
   */
  addBolt(from: Vec3, to: Vec3, element: Element, spellId?: string, mine = false): void {
    /*
     * From the wand, when it is our wand.
     *
     * Core fires the beam from the caster's eye, which is correct for hit
     * detection and wrong to look at: your own shot appeared to come out of the
     * middle of your face, passing through the viewmodel rather than leaving it.
     * The muzzle is where the wand tip actually is this frame, so the beam
     * starts where the light on the crystal is.
     *
     * Only for the local caster. Everybody else's bolt already starts at a hand
     * you can see from outside, and the muzzle is a viewmodel position that
     * means nothing for another mage.
     */
    const origin = mine && this.muzzle ? this.muzzle : from;
    const a = new THREE.Vector3(origin.x, origin.y, origin.z);
    const b = new THREE.Vector3(to.x, to.y, to.z);
    const along = b.clone().sub(a);
    const span = along.length();
    if (span < 0.1) return;

    const art = BOLT_ART[spellId ?? ''] ?? BOLT_ART[element] ?? 'light';
    const width = BOLT_WIDTH[element] ?? 0.9;
    const colour = ELEMENT_COLOR[element];

    const flash = this.takeBolt();
    const stack = flash.line as THREE.Group;
    stack.position.copy(a).addScaledVector(along, 0.5);

    /*
     * Three cards rather than one, at different widths and opacities.
     *
     * A single strip is a decal of lightning; three overlapping strips of
     * different thickness, drifting at different rates, is lightning. The eye
     * reads the disagreement between them as depth and movement — which is the
     * cheapest trick in the business and the reason nobody ships a one-quad
     * bolt. Additive, so where they cross is the brightest part, which is where
     * the core of the beam wants to be anyway.
     */
    stack.children.forEach((child, i) => {
      const layer = child as THREE.Mesh;
      const mat = layer.material as THREE.MeshBasicMaterial;
      mat.map = this.boltArt(art, i);
      mat.color.setHex(colour);
      mat.needsUpdate = true;
      layer.scale.set(width * BOLT_LAYERS[i]!.width, span, 1);
      layer.visible = true;
      /*
       * Each card starts a fixed distance along the strip, not a random one.
       *
       * They still have to disagree with each other — three identical cards is
       * one card at three times the cost — but the disagreement is now the same
       * every shot. A random start meant no two bolts down the same lane looked
       * alike, which reads as the spell wavering when what is actually wanted is
       * a weapon that does the same thing every time you pull the trigger. The
       * scroll rates in `BOLT_LAYERS` already supply the movement.
       */
      if (mat.map) mat.map.offset.y = i / BOLT_LAYERS.length;
    });

    // A flash where it landed, which is what tells the caster they connected.
    (flash.head.material as THREE.MeshBasicMaterial).color.setHex(colour);
    flash.head.position.copy(b);
    flash.head.scale.setScalar(width * 1.5);

    /*
     * And a real light there for the moment it lands.
     *
     * The beam itself lights nothing — it is additive geometry, not a source —
     * so without this a bolt striking a wall in a dark nave leaves the wall
     * exactly as dark as it was. One light on the impact is what makes the shot
     * land *in the room* rather than in front of it.
     */
    const lamp = this.boltLights[this.nextBoltLight];
    this.nextBoltLight = (this.nextBoltLight + 1) % this.boltLights.length;
    if (lamp) {
      lamp.color.setHex(colour);
      lamp.position.copy(b);
      flash.lamp = lamp;
    }

    flash.axis = along.clone().normalize();
    flash.life = ARC_SECONDS;
    stack.visible = true;
    flash.head.visible = true;
    this.faceBolt(stack, flash.axis);

    /*
     * A small burst where it lands.
     *
     * The beam and its flash say a shot happened; neither says it *arrived*. A
     * hitscan has no projectile to watch, so the moment of contact is the only
     * feedback there is, and a quarter-second strip with a blob on the end left
     * it ambiguous whether a wall had been hit or the spell had simply stopped.
     *
     * Deliberately smaller and faster than a spell's own impact — this is a
     * bolt striking, not a Fire Explosion going off, and a burst at blast scale
     * would make every bolt read as an area spell.
     */
    this.vfx.strike(b, colour, width * 1.6);
  }

  /**
   * Spins a beam card about its own axis until its face is toward the camera.
   *
   * Rebuilt every frame while the beam lives, because a quarter of a second is
   * long enough to turn in and a card that keeps its original spin goes edge-on
   * and vanishes exactly when somebody is looking at it.
   */
  private faceBolt(quad: THREE.Object3D, axis: THREE.Vector3): void {
    const eye = this.camera?.getWorldPosition(new THREE.Vector3());
    const toEye = eye ? eye.sub(quad.position).normalize() : new THREE.Vector3(0, 0, 1);

    // Across the beam and across the view: the card's own width axis.
    const wide = new THREE.Vector3().crossVectors(axis, toEye);
    if (wide.lengthSq() < 1e-4) wide.set(1, 0, 0).cross(axis);
    wide.normalize();

    const facing = new THREE.Vector3().crossVectors(wide, axis).normalize();
    quad.setRotationFromMatrix(
      new THREE.Matrix4().makeBasis(wide, axis, facing),
    );
  }

  /** A free beam card, or the oldest one. See `takeArc`. */
  private takeBolt(): Flash {
    const free = this.arcs.find((f) => f.life <= 0 && f.axis);
    if (free) return free;

    const quad = new THREE.Group();
    for (const layer of BOLT_LAYERS) {
      const card = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({
          transparent: true,
          opacity: layer.opacity,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          // Both faces: the card is spun to meet the eye every frame, but a bolt
          // fired almost straight at the camera has a moment where it is edge on
          // and the far side is what shows.
          side: THREE.DoubleSide,
        }),
      );
      quad.add(card);
    }
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.5, 10, 8),
      new THREE.MeshBasicMaterial({
        transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      }),
    );
    this.scene.add(quad, head);
    const flash: Flash = { line: quad, head, life: 0, axis: new THREE.Vector3(0, 1, 0) };
    this.arcs.push(flash);
    return this.recycle(flash, 'bolt');
  }

  /**
   * The two kinds of wall, kept warm.
   *
   * Solid and sheer differ in more than looks: one writes depth and casts a
   * shadow and the other does neither, and both of those are part of what a
   * program is compiled against. So there are two, and both are built before the
   * match rather than the first time somebody conjures one.
   */
  private barrierMaterial(solid: boolean): THREE.MeshStandardMaterial {
    const key = solid ? 'solid' : 'sheer';
    let material = this.barrierKinds.get(key);
    if (!material) {
      material = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xffffff,
        emissiveIntensity: solid ? 0.35 : 0.6,
        transparent: true,
        opacity: solid ? 0.82 : 0.26,
        roughness: 0.4,
        depthWrite: solid,
      });
      this.barrierKinds.set(key, material);
    }
    return material;
  }

  /**
   * The strip behind one layer of a beam.
   *
   * A texture is cloned per layer rather than shared, because `offset` lives on
   * the texture and not the material — three layers sharing one would scroll in
   * lockstep, which is exactly the disagreement the layering exists to create.
   * A clone shares its `source`, so the image is still uploaded to the card once
   * however many clones point at it.
   */
  private boltArt(name: string, layer = 0): THREE.Texture {
    /*
     * The file is loaded once and every layer clones *that*.
     *
     * Loading it afresh per layer — which is what asking the loader inside this
     * branch did — fetches and uploads the same strip three times over, which is
     * precisely the cost cloning was meant to avoid. A clone shares its `source`
     * with the original, so the image reaches the card once however many layers
     * point at it, while each keeps its own `offset` and can drift on its own.
     */
    let base = this.bolts.get(name);
    if (!base) {
      base = typeof document === 'undefined'
        ? new THREE.Texture()
        : new THREE.TextureLoader().load(`/sprites/bolts/${name}.jpg`);
      base.colorSpace = THREE.SRGBColorSpace;
      // Wrapping, or scrolling past the end clamps and the beam stops moving.
      base.wrapS = THREE.RepeatWrapping;
      base.wrapT = THREE.RepeatWrapping;
      this.bolts.set(name, base);
    }
    if (layer === 0) return base;

    const key = `${name}#${layer}`;
    let art = this.bolts.get(key);
    if (!art) {
      art = base.clone();
      art.needsUpdate = true;
      this.bolts.set(key, art);
    }
    return art;
  }

  addArc(from: Vec3, to: Vec3, element: Element): void {
    const colour = ELEMENT_COLOR[element];

    const flash = this.takeArc();
    const line = flash.line as THREE.Line;
    const points = line.geometry.getAttribute('position');
    points.setXYZ(0, from.x, from.y, from.z);
    points.setXYZ(1, to.x, to.y, to.z);
    points.needsUpdate = true;
    line.geometry.computeBoundingSphere();

    (line.material as THREE.LineBasicMaterial).color.setHex(colour);
    (flash.head.material as THREE.MeshBasicMaterial).color.setHex(colour);
    flash.head.position.set(to.x, to.y, to.z);
    flash.head.scale.setScalar(1);
    flash.life = ARC_SECONDS;
    flash.axis = undefined;
    line.visible = true;
    flash.head.visible = true;
  }

  /**
   * A free slot from the flash pool, or the oldest one in it.
   *
   * Every arc and every bolt used to build two fresh materials and never dispose
   * them — the geometry was cleaned up and the materials were not. At a couple of
   * hundred discharges a minute across a full match that is a steady leak of
   * GPU-backed objects with nothing to reclaim them, which is the likeliest
   * explanation for a tab dying during heavy fighting.
   *
   * The same lesson as the lights and the audio voices, arrived at from the
   * third direction: build them once, hand them out, take them back.
   */
  private takeArc(): Flash {
    const free = this.arcs.find((f) => f.life <= 0 && !f.axis);
    if (free) return free;

    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]),
      new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false }),
    );
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.32, 10, 8),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false }),
    );
    this.scene.add(line, head);
    const flash: Flash = { line, head, life: 0 };
    this.arcs.push(flash);
    return this.recycle(flash, 'arc');
  }

  /**
   * Keeps a pool from growing without bound.
   *
   * A slot is only ever created when every existing one is busy, so the pool
   * settles at the most that were ever alight at once. Past a ceiling the oldest
   * is taken instead — a discharge cut a fraction short is invisible, and an
   * unbounded pool is the leak this replaced.
   */
  private recycle(fresh: Flash, kind: 'arc' | 'bolt'): Flash {
    const mine = this.arcs.filter((f) => (kind === 'bolt') === Boolean(f.axis));
    if (mine.length <= FLASH_BUDGET) return fresh;

    let oldest = fresh;
    for (const f of mine) if (f.life < oldest.life) oldest = f;
    return oldest;
  }

  /** Where the local mage's wand tip is this frame, for the spawn blend. */
  muzzle?: THREE.Vector3;

  /**
   * The wind-up, as everyone else sees it.
   *
   * A caster is slowed and cannot be interrupted, so the only counterplay is
   * seeing it coming — which means the tell has to be visible from across the
   * nave and has to say *which school*, since a Fire Storm and a Heal Other are
   * worth very different reactions.
   */
  private syncAura(p: PlayerState, elapsed: number): void {
    const casting = p.casting;
    let aura = this.auras.get(p.id);

    if (!casting || !isEmbodied(p)) {
      // Hidden, not disposed. Every cast in the game ends, and disposing the
      // material each time gave back its compiled program — so the next cast
      // built it again. Kept, an aura costs one hidden object per mage.
      if (aura) aura.visible = false;
      return;
    }
    if (aura) aura.visible = true;

    if (!aura) {
      aura = new THREE.Mesh(
        new THREE.SphereGeometry(0.75, 14, 10),
        new THREE.MeshBasicMaterial({
          color: 0xffffff, transparent: true, opacity: 0.3,
          blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
        }),
      );
      this.scene.add(aura);
      this.auras.set(p.id, aura);
    }

    const spell = SPELLS_BY_ID[casting.spellId];
    const colour = spell ? ELEMENT_COLOR[spell.element] : 0xffffff;
    // Swells as it fills, so the tell is not just "casting" but "nearly done".
    const done = 1 - Math.max(0, casting.remaining) / Math.max(0.001, casting.total);
    const pulse = 0.85 + Math.sin(elapsed * 9) * 0.15;

    aura.position.set(p.pos.x, p.pos.y + 1.1, p.pos.z);
    aura.scale.setScalar((0.7 + done * 0.5) * pulse);
    const mat = aura.material as THREE.MeshBasicMaterial;
    mat.color.setHex(colour);
    mat.opacity = 0.16 + done * 0.3;
  }

  /**
   * A mage who is on fire looks like it.
   *
   * Both decoration and information: five seconds of burning is five seconds a
   * teammate could be healing them through, and until now the only sign of it
   * was a badge on the burning player's own screen.
   */
  private syncFlames(p: PlayerState, elapsed: number): void {
    const alight = isEmbodied(p) && p.effects.some((e) => e.spellId === 'burning');
    let fire = this.flames.get(p.id);

    if (!alight) {
      // Same reasoning as the aura: burning ends constantly, and disposing the
      // tongues took their program with them.
      if (fire) fire.visible = false;
      return;
    }
    if (fire) fire.visible = true;

    if (!fire) {
      fire = new THREE.Group();
      // Three tongues rather than a particle system. Cheap, and at any distance
      // worth reading it is the colour and the movement that carry, not detail.
      for (let i = 0; i < 3; i++) {
        const tongue = new THREE.Mesh(
          new THREE.ConeGeometry(0.16, 0.55, 6, 1, true),
          new THREE.MeshBasicMaterial({
            color: i === 0 ? 0xffd08a : ELEMENT_COLOR.fire,
            transparent: true, opacity: 0.55,
            blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
          }),
        );
        fire.add(tongue);
      }
      this.scene.add(fire);
      this.flames.set(p.id, fire);
    }

    fire.position.set(p.pos.x, p.pos.y, p.pos.z);
    fire.children.forEach((child, i) => {
      const phase = elapsed * 7 + i * 2.1;
      child.position.set(
        Math.sin(phase * 0.9) * 0.16,
        0.55 + i * 0.32 + Math.sin(phase) * 0.06,
        Math.cos(phase * 1.3) * 0.16,
      );
      child.scale.setScalar(0.75 + Math.sin(phase * 1.7) * 0.25);
      ((child as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity =
        0.4 + Math.sin(phase * 2.3) * 0.2;
    });
  }

  /**
   * Draws one of every spell effect off-screen so their shaders compile during
   * loading rather than during the first fight.
   *
   * The roster is optional and is only used for nameplates — a caller that has
   * no players to hand still gets everything else warmed, which is what the
   * tests do.
   */
  warmUp(
    at?: THREE.Vector3,
    /*
     * Anything carrying an id, a name and a side.
     *
     * A roster entry rather than a full player, because the view is filtered by
     * relevancy and at load time holds nobody but you — so warming from it warmed
     * nothing, and the nameplate program this exists to compile was first
     * compiled mid-match with a canvas rasterisation per name behind it.
     */
    players: readonly { id: string; name: string; faction: Faction }[] = [],
    localId = '',
  ): void {
    /*
     * Where the warm objects are parked, and why it is no longer under the map.
     *
     * Four hundred metres down worked for the main pass — `frustumCulled = false`
     * submits a draw call wherever a thing is, and the camera's far plane is 500,
     * so the programs were built. What it never reached was the **shadow pass**,
     * which renders from the light with a frustum of its own and compiles a
     * separate depth program per material. Nothing parked below the arena is
     * inside that frustum, so every shadow-casting material still paid for its
     * depth program the first time it came into view — which is exactly the
     * one-off microstutter on walking into a room with a crate in it.
     *
     * In front of the camera instead, where both passes can see it. The warm
     * frames are drawn behind the loading screen, so nobody watches the parade.
     */
    const park = at ?? new THREE.Vector3(0, -400, 0);
    this.vfx.warmUp(park);
    this.warmAssets();

    /*
     * The sprites, which nothing above reaches.
     *
     * Everything else warmed here is a mesh or a line, and `SpriteMaterial`
     * shares a program with neither — so however thoroughly the spells were
     * prepared, the first `Sprite` in the scene still compiled one. Nothing
     * creates a sprite until a teammate comes within nameplate range or the
     * player lands a hit, which put the compile a second or two into the match
     * and then a second, smaller one at the first exchange: the nameplate paid
     * for the program, and the first damage number paid for a canvas and an
     * upload on top of it.
     *
     * Both are warmed together because they are the same cost twice.
     */
    this.nameplates.warmUp(players, localId, park);
    this.numbers.warmUp(park);

    /*
     * Everything else a fight can put on screen, built once and kept.
     *
     * The spell effects were warmed and the rest was not, which is why the
     * hitching started the moment somebody cast rather than the moment the match
     * did: an arc is a `Line`, which is a different program from any mesh; a
     * caster's aura and a burning mage are additive double-sided shells; a wall
     * is a transparent standard material, and a solid one casts a shadow, which
     * is a depth program of its own.
     *
     * All parked below the arena and hidden after the warm frames. Never
     * disposed — Three.js frees a program when its last material goes, so
     * tidying up here would undo the whole exercise.
     */
    const below = park.clone();
    const colour = ELEMENT_COLOR.light;

    // An arc: a line and a head, which no other effect in the game uses.
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([below, below.clone().add(new THREE.Vector3(0, 1, 0))]),
      new THREE.LineBasicMaterial({ color: colour, transparent: true, depthWrite: false }),
    );
    this.warm.push(line);

    /*
     * One beam card per strip, which is one per distinct texture.
     *
     * The arcs were warmed and the *bolts* were not, so the first Mana Bolt of a
     * match paid for its texture upload and its program in the middle of a
     * fight. Every strip has to be built here because a texture is uploaded on
     * first draw, not on first load — the download during the loading screen is
     * only half of it.
     */
    for (const art of new Set(Object.values(BOLT_ART))) {
      // Every layer of every strip. Each layer holds its own clone of the
      // texture so the three can drift apart, and a clone is its own upload as
      // far as the first draw is concerned even though the image is shared.
      BOLT_LAYERS.forEach((layer, i) => {
        const card = new THREE.Mesh(
          new THREE.PlaneGeometry(1, 1),
          new THREE.MeshBasicMaterial({
            map: this.boltArt(art, i),
            transparent: true,
            opacity: layer.opacity,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
            side: THREE.DoubleSide,
          }),
        );
        card.position.copy(below);
        card.scale.set(layer.width, 8, 1);
        this.warm.push(card);
      });
    }

    /*
     * Both kinds of wall. In front of the camera rather than parked below,
     * because a solid one casts a shadow and a shadow program is only compiled
     * for something the shadow camera can actually see.
     */
    for (const solid of [true, false]) {
      const wall = new THREE.Mesh(
        new THREE.BoxGeometry(2, 2, 0.3),
        this.barrierMaterial(solid).clone(),
      );
      wall.position.copy(below);
      wall.castShadow = solid;
      wall.receiveShadow = true;
      this.warm.push(wall);
    }

    // A caster's aura and a burning mage: additive shells, both double-sided.
    for (const geometry of [new THREE.SphereGeometry(0.75, 14, 10),
      new THREE.ConeGeometry(0.16, 0.55, 6, 1, true)]) {
      this.warm.push(new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
        color: colour, transparent: true, opacity: 0.3,
        blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
      })));
    }

    // Walls, in both their forms. The solid one casts, which is a second program.
    for (const solid of [true, false]) {
      const wall = new THREE.Mesh(
        new THREE.BoxGeometry(6, 3.5, 0.6),
        new THREE.MeshStandardMaterial({
          color: colour, emissive: colour, emissiveIntensity: solid ? 0.35 : 0.6,
          transparent: true, opacity: solid ? 0.82 : 0.26,
          roughness: 0.4, depthWrite: solid,
        }),
      );
      wall.castShadow = solid;
      wall.receiveShadow = true;
      this.warm.push(wall);
    }

    /*
     * Signs and storms: flat discs, rings, and the open-ended column a storm
     * churns inside. The column was missing, which is half of why a Firestorm
     * hitched — the other half being the light it used to build for itself.
     */
    for (const geometry of [
      new THREE.CircleGeometry(1, 40),
      new THREE.RingGeometry(0.8, 1, 28),
      new THREE.CylinderGeometry(1, 1, 4, 28, 1, true),
    ]) {
      this.warm.push(new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
        color: colour, transparent: true, opacity: 0.4,
        side: THREE.DoubleSide, depthWrite: false,
      })));
    }

    for (const object of this.warm) {
      object.position.copy(below);
      // Culling would defeat the whole point: parked four hundred metres under
      // the arena, every one of these is outside the frustum and would never be
      // submitted, so no program would be built. The warm frames are drawn
      // behind the loading screen, so nobody sees them.
      object.frustumCulled = false;
      object.traverse((n) => { n.frustumCulled = false; });
      this.scene.add(object);
    }
  }

  /**
   * One of every model the arena can put on screen, drawn once during loading.
   *
   * Level geometry is built before this runs, so its materials are already in
   * the scene and `compileAsync` has seen them — but only from where the player
   * is standing, and only for the main pass. A crate on the far side of the
   * cathedral is culled during the warm frames and never reaches the shadow
   * camera, so it compiles its depth program the moment it is first walked past.
   * One stutter per prop per session, which is precisely what was being seen.
   *
   * Cloned here, in front of the camera, casting and receiving as it will in
   * play, and then hidden. Kept forever: releasing the last material that uses a
   * program gives the program back, so tidying up would undo the work.
   */
  private warmAssets(): void {
    for (const role of WARMED_ROLES) {
      // Roles with no model behind them fall back to procedural geometry that is
      // already warmed elsewhere; building those again would be busywork.
      if (!this.assets.usesModel(role)) continue;
      const model = this.assets.create({ role, size: { x: 1, y: 1, z: 1 } });
      model.traverse((n) => {
        n.castShadow = true;
        n.receiveShadow = true;
      });
      this.warm.push(model);
    }
  }

  /**
   * Drains the colour out of a mage, or puts it back.
   *
   * Transparency alone was never quite enough: a faint Chaos mage is still
   * unmistakably a Chaos mage, so at any distance a ghost read as a living enemy
   * wearing less. Colour is what identifies somebody in this game — the whole
   * faction system is colour — so taking it away is what says *dead* in a
   * language the player already speaks.
   *
   * Done by tinting rather than by swapping textures. A material's `color`
   * multiplies its map, so a grey tint drains a texture toward grey without
   * touching the texture itself; and `color` is not part of the program
   * signature, so nothing recompiles. Swapping maps would mean a second copy of
   * every mage texture in memory and a shader rebuild at the worst moment.
   *
   * The original tint is remembered per material, because a faction's robe is
   * already tinted and putting back plain white on resurrection would leave a
   * mage in the wrong colours for the rest of the match.
   */
  private dress(mesh: THREE.Object3D, id: string, ghost: boolean): void {
    let living = this.livingTint.get(id);
    if (!living) {
      living = new Map();
      eachMaterial(mesh, (mat) => {
        const tinted = mat as THREE.MeshStandardMaterial;
        if (tinted.color) living!.set(tinted.uuid, tinted.color.getHex());
      });
      this.livingTint.set(id, living);
    }

    eachMaterial(mesh, (mat) => {
      mat.opacity = ghost ? GHOST_OPACITY : 1;
      const tinted = mat as THREE.MeshStandardMaterial;
      if (!tinted.color) return;

      if (ghost) {
        tinted.color.setHex(GHOST_GREY);
      } else {
        tinted.color.setHex(living!.get(tinted.uuid) ?? 0xffffff);
      }
      // Nothing dead should catch the light the way the living do.
      if (tinted.emissive) tinted.emissiveIntensity = ghost ? 0 : 1;
    });
  }

  /**
   * Hands the storm light pool to whichever storms are burning.
   *
   * A storm used to build its own `PointLight` when it spawned, which is the
   * stutter on the first Firestorm of a match — and not a small one. Three.js
   * compiles a material against the *number of visible lights* in the scene and
   * caches programs by it, so a new light invalidates every lit material there
   * is: every wall, floor, pillar, prop and mage, in the middle of a fight.
   *
   * The same fault the spell lights and the torches each had in turn, and the
   * same fix. The pool exists from the moment the arena is built and never
   * changes size; a slot with no storm in it is dimmed to nothing rather than
   * hidden, because hiding it would change the count all over again.
   */
  private lightStorms(state: SimState): void {
    const burning = state.storms.slice(0, this.stormLights.length);

    this.stormLights.forEach((light, i) => {
      const storm = burning[i];
      if (!storm) {
        light.intensity = 0;
        return;
      }
      light.color.setHex(ELEMENT_COLOR[storm.element]);
      light.distance = storm.radius * 2.5;
      light.position.set(storm.pos.x, storm.pos.y + 1.6, storm.pos.z);
      // Fading with the storm, so the last second dims rather than snapping out.
      light.intensity = STORM_LIGHT * Math.min(1, storm.remaining);
    });
  }

  /** Hidden, never disposed. See warmUp. */
  coolDown(): void {
    this.vfx.coolDown();
    this.nameplates.coolDown();
    this.numbers.coolDown();
    for (const object of this.warm) object.visible = false;
  }

  /**
   * Drops everything belonging to the round that has ended.
   *
   * Called when a set rotates to another map. Spell effects, bolts, arcs and
   * floating numbers are all mid-flight over geometry that is about to be
   * replaced, and a bolt continuing across a map it was not cast on is a ghost
   * of the last round drawn over this one.
   *
   * The pools themselves stay. Every one of these was warmed during loading and
   * their programs are compiled; this puts the objects away rather than
   * disposing them, which is the same rule the warm-up follows and for the same
   * reason.
   */
  clearTransient(): void {
    this.vfx.coolDown();
    this.numbers.coolDown();
  }

  /** Impacts are events, not state — there is nothing left to mirror. */
  ingest(events: readonly SimEvent[], players: readonly PlayerState[], localId: string): void {
    this.vfx.ingest(events);
    this.numbers.ingest(events, players, localId);
  }

  /**
   * How far into the unsimulated tick the renderer is drawing, 0 to 1.
   *
   * Set by the frame loop from the transport's leftover accumulator. Defaults to
   * 1 — the authoritative position — so anything that does not set it behaves
   * exactly as before.
   */
  alpha = 1;

  update(state: SimState, localId: string, elapsed: number, dt = 1 / 60): void {
    for (const pool of state.arena.pools) {
      const v = this.pools.get(pool.id);
      if (!v) continue;
      const colour = pool.owner === 'neutral' ? NEUTRAL : FACTION_COLOR[pool.owner];
      const mat = v.disc.material as THREE.MeshStandardMaterial;
      mat.color.setHex(colour);
      mat.emissive.setHex(colour);
      mat.emissiveIntensity = 0.4 + 0.25 * Math.sin(elapsed * 2 + pool.pos.x);
      v.light.color.setHex(colour);
      v.light.intensity = pool.owner === 'neutral' ? 1.4 : 3.4;

      /*
       * The ring says who holds the pool, and sweeps while somebody takes it.
       *
       * It used to be drawn purely from `progressFaction`, which is only set
       * while a capture is actually running — so the instant one finished the
       * ring went grey and stayed grey, and a pool you owned looked exactly like
       * one nobody did. The colour a player reads at a glance across a room is
       * the *owner*; the sweep is the thing that happens on top of it.
       */
      const capturing = pool.progress > 0 && pool.progressFaction !== 'neutral';
      const ringColour = capturing
        ? (FACTION_COLOR as Record<string, number>)[pool.progressFaction] ?? colour
        : colour;
      (v.ring.material as THREE.MeshBasicMaterial).color.setHex(ringColour);

      /*
       * Rebuilt only when the sweep has actually moved.
       *
       * This allocated and disposed a `RingGeometry` for every pool on every
       * frame — eight geometries a frame, sixty times a second, each one
       * uploading a fresh vertex buffer and leaving the last for the collector.
       * Nothing about a pool nobody is standing in changes from frame to frame,
       * and the garbage was landing in the middle of fights.
       *
       * Quantised to a hundredth of a turn, which is finer than the ring can
       * show at any distance a player sees it from.
       */
      const sweep = capturing ? Math.round(pool.progress * 100) / 100 : 1;
      if (v.sweep !== sweep) {
        v.sweep = sweep;
        v.ring.geometry.dispose();
        v.ring.geometry = new THREE.RingGeometry(
          pool.innerRadius + 0.15, pool.innerRadius + 0.5, 48, 1,
          Math.PI / 2, -Math.PI * 2 * sweep,
        );
      }
    }

    for (const shrine of state.arena.shrines) {
      const v = this.shrines.get(shrine.id);
      if (!v) continue;
      const ratio = shrine.power / SHRINE_POWER_MAX;
      if (shrine.conquered) {
        eachMaterial(v.crystal, (mat) => {
          mat.color?.setHex(0x555049);
          mat.emissive?.setHex(0x555049);
          mat.emissiveIntensity = 0.15;
        });
        v.light.intensity = 0.5;
      } else {
        const colour = FACTION_COLOR[shrine.faction];
        eachMaterial(v.crystal, (mat) => {
          mat.color?.setHex(colour);
          mat.emissive?.setHex(colour);
          mat.emissiveIntensity = 0.4 + 1.2 * ratio;
        });
        v.light.intensity = 3 + 12 * ratio;
      }
      v.crystal.rotation.y = elapsed * 0.4;
      v.crystal.position.y = shrine.pos.y + 2.4 + Math.sin(elapsed * 1.3) * 0.12;
    }

    // Spells in flight, their trails, their impacts and their debris. All of it
    // lives in SpellVfx, which owns the light budget as well — a point light per
    // projectile is the obvious approach and the one that would cost the frame
    // rate in a busy fight.
    if (this.camera) {
      this.vfx.syncProjectiles(state, this.camera, dt, localId, this.muzzle);
      this.nameplates.update(state.players, localId, this.camera);
    }
    this.vfx.tick(dt);
    this.numbers.tick(dt);

    // Arcs fade out over a quarter second. They are the only thing here not
    // mirrored from state — an arc is an instant, not an object, so the event
    // that reports it is all there is to draw.
    for (let i = this.arcs.length - 1; i >= 0; i--) {
      const arc = this.arcs[i];
      arc.life -= dt;
      const fade = Math.max(0, arc.life / ARC_SECONDS);

      const drawn = arc.line as THREE.Mesh;
      if (drawn.material) {
        (drawn.material as THREE.Material).opacity = fade;
      } else {
        /*
         * A stack of cards, each fading from its own base and each drifting at
         * its own rate. The scroll is what turns a stamped strip into something
         * travelling, and the different rates are what stop three cards reading
         * as one.
         */
        arc.line.children.forEach((child, i) => {
          const layer = child as THREE.Mesh;
          const mat = layer.material as THREE.MeshBasicMaterial;
          mat.opacity = fade * (BOLT_LAYERS[i]?.opacity ?? 1);
          if (mat.map) mat.map.offset.y -= (BOLT_LAYERS[i]?.scroll ?? 1) * dt;
        });
      }

      (arc.head.material as THREE.MeshBasicMaterial).opacity = fade * 0.85;
      arc.head.scale.setScalar(1 + (1 - fade) * 1.6);
      // Dimmed rather than switched off: the count of *visible* lights is part
      // of every material's program signature, so a lamp must never be hidden.
      if (arc.lamp) arc.lamp.intensity = BOLT_LIGHT * fade;

      // A beam card is spun to meet the eye every frame it lives. A quarter of a
      // second is long enough to turn in, and a card that keeps the spin it was
      // born with goes edge-on and vanishes exactly when somebody looks at it.
      if (arc.axis) this.faceBolt(arc.line, arc.axis);

      if (arc.life > 0) continue;

      // Hidden, not destroyed. The slot goes back to the pool with its geometry
      // and its materials intact, which is the whole point of having one.
      arc.line.visible = false;
      arc.head.visible = false;
    }

    // Barriers. Solid ones read nearly opaque; the ones you can push through are
    // deliberately sheer, because a wall that looks solid and is not is a lie the
    // player pays for. Rapier colliders for the solid ones are GameWorld's job.
    const liveBarriers = new Set<number>();
    for (const b of state.barriers) {
      liveBarriers.add(b.id);
      let mesh = this.barriers.get(b.id);
      if (!mesh) {
        const colour = ELEMENT_COLOR[b.element];
        /*
         * Cloned from a warmed original, not built fresh.
         *
         * A barrier is the only *lit* thing a spell makes — a standard material
         * with emissive, transparency and a shadow to cast — and a lit material
         * compiles against the scene's light count and shadow setup, which makes
         * it a far heavier compile than the unlit flashes everything else uses.
         * Built on first cast, it was a hitch in the moment before a Wall of Ice
         * appeared.
         *
         * Each wall still needs its own material, because they fade out on their
         * own clocks. A clone of an already-compiled program costs nothing.
         */
        const material = this.barrierMaterial(b.blocksBodies).clone();
        material.color.setHex(colour);
        material.emissive.setHex(colour);

        mesh = new THREE.Mesh(
          new THREE.BoxGeometry(b.size.x, b.size.y, b.size.z),
          material,
        );
        mesh.position.set(b.pos.x, b.pos.y, b.pos.z);
        mesh.rotation.y = b.yaw;
        // Only the solid ones. A sheer Airwall casting a hard shadow would
        // promise cover it does not provide.
        mesh.castShadow = b.blocksBodies;
        mesh.receiveShadow = true;
        this.scene.add(mesh);
        this.barriers.set(b.id, mesh);
      }
      // Fade out over the last second rather than blinking out of existence.
      const mat = mesh.material as THREE.MeshStandardMaterial;
      const base = b.blocksBodies ? 0.82 : 0.26;
      mat.opacity = base * Math.min(1, b.remaining);
    }
    for (const [id, mesh] of this.barriers) {
      if (liveBarriers.has(id)) continue;
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      this.barriers.delete(id);
    }

    // Signs. Team-coloured and deliberately dim — findable if you are looking
    // for one, missable if you are not. The disc is drawn to the simulation's
    // own trigger radius rather than a decorative one, so what you see is
    // exactly what you step on.
    const liveSigns = new Set<number>();
    for (const sign of state.signs) {
      liveSigns.add(sign.id);
      let group = this.signs.get(sign.id);
      if (!group) {
        // Expulsion is white for everyone. It launches whoever touches it,
        // including the Arcanist who laid it, so colouring it by team would
        // promise an allegiance it does not have.
        const colour = sign.spellId === 'sign_expulse'
          ? 0xf0f0f0
          : FACTION_COLOR[sign.faction];
        group = new THREE.Group();

        const disc = new THREE.Mesh(
          new THREE.CircleGeometry(sign.triggerRadius, 28),
          new THREE.MeshBasicMaterial({
            color: colour, transparent: true, opacity: 0.3, depthWrite: false,
          }),
        );
        disc.rotation.x = -Math.PI / 2;
        group.add(disc);

        const rim = new THREE.Mesh(
          new THREE.RingGeometry(sign.triggerRadius * 0.82, sign.triggerRadius, 28),
          new THREE.MeshBasicMaterial({
            color: colour, transparent: true, opacity: 0.55,
            side: THREE.DoubleSide, depthWrite: false,
          }),
        );
        rim.rotation.x = -Math.PI / 2;
        group.add(rim);

        group.position.set(sign.pos.x, sign.pos.y + 0.03, sign.pos.z);
        this.scene.add(group);
        this.signs.set(sign.id, group);
      }
      // A slow pulse, and a fade over the last two seconds of its life. No
      // point light: a sign that lights the floor around it is not subtle.
      const pulse = 0.82 + Math.sin(elapsed * 1.7 + sign.id) * 0.18;
      const fade = Math.min(1, sign.remaining / 2);
      const arming = sign.armFor > 0 ? 0.45 : 1;
      const [disc, rim] = group.children as THREE.Mesh[];
      (disc.material as THREE.MeshBasicMaterial).opacity = 0.3 * pulse * fade * arming;
      (rim.material as THREE.MeshBasicMaterial).opacity = 0.55 * pulse * fade * arming;
    }
    for (const [id, group] of this.signs) {
      if (liveSigns.has(id)) continue;
      // Geometry only. A sign's material holds its compiled program, and signs
      // are laid and triggered throughout a match.
      this.scene.remove(group);
      for (const child of group.children) (child as THREE.Mesh).geometry.dispose();
      this.signs.delete(id);
    }

    // Storms. Same mirror pattern as projectiles: core owns the list, this only
    // draws it. The column is drawn to the simulation's own radius and height so
    // that what looks like cover is exactly what is cover.
    const liveStorms = new Set<number>();
    for (const storm of state.storms) {
      liveStorms.add(storm.id);
      let group = this.storms.get(storm.id);
      if (!group) {
        const colour = ELEMENT_COLOR[storm.element];
        group = new THREE.Group();

        const column = new THREE.Mesh(
          new THREE.CylinderGeometry(storm.radius, storm.radius, storm.height, 28, 1, true),
          new THREE.MeshBasicMaterial({
            color: colour, transparent: true, opacity: 0.16,
            side: THREE.DoubleSide, depthWrite: false,
          }),
        );
        column.position.y = storm.height / 2;
        group.add(column);

        const floor = new THREE.Mesh(
          new THREE.CircleGeometry(storm.radius, 40),
          new THREE.MeshBasicMaterial({
            color: colour, transparent: true, opacity: 0.34, depthWrite: false,
          }),
        );
        floor.rotation.x = -Math.PI / 2;
        floor.position.y = 0.05;
        group.add(floor);

        group.position.set(storm.pos.x, storm.pos.y, storm.pos.z);
        this.scene.add(group);
        this.storms.set(storm.id, group);
      }
      // Churn, and a fade over the last second so it does not simply vanish.
      group.rotation.y = elapsed * 0.9;
      const fade = Math.min(1, storm.remaining);
      for (const child of group.children) {
        const mat = (child as THREE.Mesh).material as THREE.Material | undefined;
        if (mat && 'opacity' in mat) {
          (mat as THREE.MeshBasicMaterial).opacity =
            ((child as THREE.Mesh).geometry instanceof THREE.CircleGeometry ? 0.34 : 0.16) * fade;
        }
      }
    }
    for (const [id, group] of this.storms) {
      if (liveStorms.has(id)) continue;
      this.scene.remove(group);
      for (const child of group.children) (child as THREE.Mesh).geometry?.dispose();
      this.storms.delete(id);
    }

    this.lightStorms(state);

    /*
     * Everybody the view still contains, so the rest can be put away.
     *
     * `state.players` is filtered by relevancy: a mage you cannot see is not in
     * it. This loop only ever *added* and updated, so a mage who walked out of
     * sight left their body behind — standing in the scene at the last position,
     * in the last pose, at the last opacity, for the rest of the match.
     *
     * Which is three bugs wearing one cause. A bot mid-stride when it left your
     * view is a bot frozen mid-stride. A mage who then dies somewhere you cannot
     * see never plays the death you were not there for, so their ghost is left
     * standing upright instead of falling. And the whole effect only appears in
     * multiplayer, because a loopback room hands over an unfiltered state and
     * nobody ever drops out of it.
     */
    const present = new Set(state.players.map((p) => p.id));
    for (const [id, mesh] of this.bodies) {
      if (present.has(id)) continue;
      /*
       * Hidden, not disposed — unless they have actually left the match.
       *
       * Somebody out of sight is usually about to be in sight again, and
       * rebuilding eleven meshes with cloned materials is the stall that the
       * whole loading-screen warm-up exists to avoid. The roster says who is
       * still in the room, and that is what separates "behind a pillar" from
       * "disconnected".
       */
      mesh.visible = false;
      /*
       * And whatever was attached to them.
       *
       * A casting aura and a burning mage's flames are separate objects keyed by
       * the same id, updated only from inside the loop below — so a mage who
       * left your view mid-cast left a spell-glow hanging in the air over a body
       * that was not moving, which is its own small haunting.
       */
      const aura = this.auras.get(id);
      if (aura) aura.visible = false;
      const flame = this.flames.get(id);
      if (flame) flame.visible = false;

      const stillPlaying = state.roster?.some((m) => m.id === id) ?? true;
      if (stillPlaying) continue;

      this.scene.remove(mesh);
      this.bodies.delete(id);
      this.outfits.delete(id);
      this.animators.delete(id);
      this.shadowState.delete(id);
      this.ghostState.delete(id);
      if (aura) { this.scene.remove(aura); this.auras.delete(id); }
      if (flame) { this.scene.remove(flame); this.flames.delete(id); }
    }

    // Other mages. First person, so the local player has no visible body.
    this.frame += 1;
    let index = -1;
    for (const p of state.players) {
      if (p.id === localId) continue;
      index += 1;
      let mesh = this.bodies.get(p.id);
      /*
       * Built for one side and discipline, so rebuilt if either changes.
       *
       * A mage's model is created once, the first time they appear, and dressed
       * in that moment's faction. A lobby reseat, a seat reused by a bot on
       * another side, or a balancing move between rounds left the old robes on —
       * which is how an Order mage came to be wearing Chaos red.
       */
      const outfit = `${p.cls}/${p.faction}`;
      if (mesh && this.outfits.get(p.id) !== outfit) {
        this.scene.remove(mesh);
        this.bodies.delete(p.id);
        this.animators.delete(p.id);
        this.shadowState.delete(p.id);
        this.ghostState.delete(p.id);
        mesh = undefined;
      }
      if (!mesh) {
        this.outfits.set(p.id, outfit);
        mesh = this.assets.create({
          role: mageRole(p.cls), color: FACTION_COLOR[p.faction], faction: p.faction,
        });
        // Casting is most of what makes a mage read as standing in the room
        // rather than composited over it.
        mesh.traverse((n) => { n.castShadow = true; n.receiveShadow = true; });
        this.shadowState.set(p.id, true);
        // Transparent from the start, at full opacity. Costs a place in the
        // sorted queue and saves a recompile every time somebody dies.
        eachMaterial(mesh, (mat) => {
          mat.transparent = true;
          mat.opacity = 1;
        });
        this.scene.add(mesh);
        this.bodies.set(p.id, mesh);

        const clips = this.assets.clipsFor(mageRole(p.cls));
        if (Object.keys(clips).length) {
          this.animators.set(p.id, new MageAnimator(
            mesh, clips, this.assets.clipSpeedsFor(mageRole(p.cls)),
          ));
        }
      }
      // Everything the registry hands back is anchored at the base, so a loaded
      // mage and a fallback capsule are placed by the same line.
      /*
       * A ghost hovers. It is drifting toward a Cleric hoping to be called
       * back, and something that moves along the floor at head height reads as a
       * body being dragged rather than a spirit — which is exactly how it looked
       * before the animator learned to stand them up.
       */
      const float = p.ghost ? GHOST_FLOAT + Math.sin(elapsed * 1.6 + this.bob(p.id)) * 0.12 : 0;
      /*
       * Drawn between the last two simulated positions, like the camera.
       *
       * Every mage in the match moves at the simulation's sixty hertz, so on a
       * fast monitor they all hold still and jump together. Interpolating costs
       * one tick of visual lag and is what makes a bot crossing a nave look like
       * it is walking rather than being stepped along.
       */
      const was = p.prevPos ?? p.pos;
      mesh.position.set(
        was.x + (p.pos.x - was.x) * this.alpha,
        was.y + (p.pos.y - was.y) * this.alpha + float,
        was.z + (p.pos.z - was.z) * this.alpha,
      );

      /*
       * How much of this mage to draw.
       *
       * Each is eleven to fourteen meshes with cloned materials, so two dozen of
       * them is three hundred skinned primitives — and with shadows on, six
       * hundred draw calls. That is thirty milliseconds of main thread against a
       * sixteen millisecond budget, which is why the game stuttered in
       * proportion to the number of bots and recovered as they died.
       */
      const distance = this.camera
        ? this.camera.position.distanceTo(mesh.position)
        : 0;
      const lod = lodFor(distance, this.frame, index);

      mesh.visible = lod.draw && !p.inValhalla;
      if (!lod.draw) continue;

      if (this.shadowState.get(p.id) !== lod.shadow) {
        setShadowCasting(mesh, lod.shadow);
        this.shadowState.set(p.id, lod.shadow);
      }

      // Posing a skeleton nobody can make out is the other half of the cost.
      const animator = this.animators.get(p.id);
      if (lod.animate) animator?.update(p, dt * lod.dtScale);

      /*
       * Aim, plus however far the body has turned toward where it is going.
       *
       * Applied here rather than inside the animator because the mesh's rotation
       * belongs to whoever placed it, and set *after* the pose so the lean is
       * this frame's rather than last frame's. `p.yaw` is untouched: the body
       * leans, the aim does not, which is the whole point of doing it this way
       * instead of turning the mage.
       */
      mesh.rotation.y = p.yaw + (animator?.leanYaw ?? 0);
      /*
       * Opacity only, and only when it has changed.
       *
       * `transparent` is deliberately not touched here. It is part of the key
       * Three.js compiles a program against, so flipping it on death threw away
       * every one of that mage's programs and built them again — which is
       * exactly the hitch that arrived the moment somebody died. The materials
       * are made transparent once at spawn, at full opacity, and only the
       * opacity moves.
       */
      if (this.ghostState.get(p.id) !== p.ghost) {
        this.ghostState.set(p.id, p.ghost);
        this.dress(mesh, p.id, p.ghost);
      }

      this.syncAura(p, elapsed);
      this.syncFlames(p, elapsed);
    }
  }
}
